/**
 * SettingsPage — /settings and /settings/:section
 * Settings on the SettingsPage template: icon-tile section nav on the left, the active section on the right.
 * Demo pages: /profile (nav + account form), /billing-and-plan, /connected-apps.
 * Template example: NextAdmin `profile/layout.tsx`. Each section lives at its own URL, so a section can be linked to.
 */
import React, { Suspense } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, KeyRound, SlidersHorizontal, UserRound, UsersRound } from 'lucide-react';
import { getMeOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { Skeleton } from '../../components/ui/Skeleton.js';
import { QueryBoundary } from '../../components/ui/QueryBoundary.js';
import { SettingsPage as SettingsTemplate, type SettingsNavItem } from '../../components/page/index.js';
import { ApproverDelegationPanel } from '../../components/approval/ApproverDelegationPanel.js';
import { usePolicy } from '../../hooks/usePolicy.js';
import { useIsModuleEnabled } from '../../modules/useEnabledModules.js';
import { ProfileTab } from './ProfileTab.js';
import { PreferencesTab } from './PreferencesTab.js';
import { NotificationsTab } from './NotificationsTab.js';

// Exchange keys are finance-domain: lazy-load the tab and show it only when the organization has the finance module,
// so a non-finance organization's bundle and UI never carry it.
const ExchangeKeysTab = React.lazy(() =>
  import('../../../../modules/finance/client/pages/settings/ExchangeKeysTab.js').then((m) => ({
    default: m.ExchangeKeysTab,
  })),
);

type SectionId = 'profile' | 'preferences' | 'notifications' | 'exchange-keys' | 'delegations';

interface Section extends SettingsNavItem {
  id: SectionId;
}

const ICON_SIZE = 20;

const PROFILE: Section = {
  id: 'profile',
  to: '/settings',
  title: 'Profile',
  description: 'Your name and sign-in details',
  icon: <UserRound size={ICON_SIZE} />,
};
const PREFERENCES: Section = {
  id: 'preferences',
  to: '/settings/preferences',
  title: 'Preferences',
  description: 'Theme, time zone and trading defaults',
  icon: <SlidersHorizontal size={ICON_SIZE} />,
};
const NOTIFICATIONS: Section = {
  id: 'notifications',
  to: '/settings/notifications',
  title: 'Notifications',
  description: 'Where Conflui sends alerts',
  icon: <Bell size={ICON_SIZE} />,
};
const EXCHANGE_KEYS: Section = {
  id: 'exchange-keys',
  to: '/settings/exchange-keys',
  title: 'Exchange keys',
  description: 'API keys for your exchange accounts',
  icon: <KeyRound size={ICON_SIZE} />,
};
const DELEGATIONS: Section = {
  id: 'delegations',
  to: '/settings/delegations',
  title: 'Delegations',
  description: 'Who can approve for you while you are away',
  icon: <UsersRound size={ICON_SIZE} />,
};

function SettingsSkeleton() {
  return (
    <div className="space-y-5" role="status" aria-label="Loading settings">
      <Skeleton height={28} width="30%" />
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Skeleton height={44} rounded="rounded-lg" />
        <Skeleton height={44} rounded="rounded-lg" />
        <Skeleton height={44} rounded="rounded-lg" />
        <Skeleton height={44} rounded="rounded-lg" />
      </div>
    </div>
  );
}

export const SettingsPage: React.FC = () => {
  const { section } = useParams<{ section?: string }>();
  const canManageNotifications = usePolicy('CanManageNotifications');
  const canManageDelegations = usePolicy('CanManageApproverDelegations');
  const isFinanceEnabled = useIsModuleEnabled('finance') === true;
  const meQuery = useQuery(getMeOptions());

  const sections: Section[] = [
    PROFILE,
    PREFERENCES,
    ...(canManageNotifications ? [NOTIFICATIONS] : []),
    ...(isFinanceEnabled ? [EXCHANGE_KEYS] : []),
    ...(canManageDelegations ? [DELEGATIONS] : []),
  ];
  const active = sections.find((s) => s.id === (section ?? 'profile'));
  if (!active) return <Navigate to="/settings" replace />;

  return (
    <SettingsTemplate title="Settings" subtitle="Manage your account and how Conflui works for you." nav={sections}>
      {active.id === 'profile' && (
        <QueryBoundary query={meQuery} label="your profile" loading={<SettingsSkeleton />}>
          {(data) => <ProfileTab user={data.user} />}
        </QueryBoundary>
      )}
      {active.id === 'preferences' && (
        <QueryBoundary query={meQuery} label="your preferences" loading={<SettingsSkeleton />}>
          {(data) => <PreferencesTab user={data.user} />}
        </QueryBoundary>
      )}
      {active.id === 'notifications' && <NotificationsTab />}
      {active.id === 'exchange-keys' && (
        <Suspense fallback={<SettingsSkeleton />}>
          <ExchangeKeysTab />
        </Suspense>
      )}
      {active.id === 'delegations' && <ApproverDelegationPanel />}
    </SettingsTemplate>
  );
};
